// ContextualHelp.tsx - Intelligent contextual help system
// Help button on every page with page-specific guidance

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X, Lightbulb, BookOpen, Sparkles, Info } from 'lucide-react';
import './ContextualHelp.css';

// Page-specific help content
interface PageHelp {
  title: string;
  description: string;
  tips: string[];
  whatsNew?: string[];
}

const PAGE_HELP: Record<string, PageHelp> = {
  '/': {
    title: 'Dashboard',
    description: "Your Cipher Intelligence Dashboard. The signal cards at the top show proprietary intelligence about current market conditions. Click any card to see the full analysis and underlying data.",
    tips: [
      'The three intelligence signals update in real-time based on market conditions.',
      'Your watchlist summary shows stocks with the highest Cipher Scores.',
      'The narrative intelligence card provides a human-readable market summary.',
    ],
    whatsNew: [
      'New: Data Storytelling integration with contextual insights',
      'New: Personalized greeting based on your usage patterns',
    ],
  },
  '/watchlist': {
    title: 'Watchlist',
    description: "Your watchlist monitors stocks you care about. Cipher calculates live scores, tracks news impact, and alerts you to significant changes. Add up to 999 tickers.",
    tips: [
      'Sort by Cipher Score to see your highest-rated opportunities.',
      'Click any ticker to see the full stock analysis page.',
      'Use the search bar to quickly add new stocks to track.',
    ],
    whatsNew: [
      'New: Smart suggestions for stocks you research frequently',
    ],
  },
  '/portfolio': {
    title: 'Portfolio',
    description: "Track your real investments here. Enter your positions and Cipher will show you real-time P&L, risk metrics, and AI analysis of your holdings. Your data is stored securely and never shared.",
    tips: [
      'Add positions with your cost basis to track actual performance.',
      'The AI narrative provides insights about your portfolio composition.',
      'Risk metrics help you understand concentration and diversification.',
    ],
    whatsNew: [
      'New: Portfolio narrative with AI-powered analysis',
    ],
  },
  '/screener': {
    title: 'Screener',
    description: "Find investment opportunities using powerful filters. Screen by Cipher Score, fundamentals, technicals, and more. Save your favorite screens for quick access.",
    tips: [
      'Use preset screens like Warren Buffett Style to get started quickly.',
      'Cipher Score filter finds our highest-conviction opportunities.',
      'Export results to CSV for further analysis.',
    ],
    whatsNew: [
      'New: Intelligence bar summarizing your screen results',
    ],
  },
  '/strategy-studio': {
    title: 'Strategy Studio',
    description: "Write and backtest trading strategies using real historical data. Use the 8 built-in templates to get started, or write your own Python-like logic. Cipher Score and causal event signals are available as inputs — no other platform offers this.",
    tips: [
      'Start with a template strategy and modify to your preferences.',
      'Backtest periods of 2-5 years give reliable performance estimates.',
      'Compare your strategy performance against buy-and-hold benchmarks.',
    ],
    whatsNew: [
      'New: Strategy narrative explaining backtest results in plain English',
    ],
  },
  '/alerts': {
    title: 'Alerts Center',
    description: "Set up alerts for price movements, technical signals, earnings events, and more. Cipher will notify you when your conditions are met via browser notifications.",
    tips: [
      'Price alerts trigger when a stock crosses your specified threshold.',
      'Earnings alerts remind you before quarterly reports.',
      'You can combine multiple conditions for sophisticated alerts.',
    ],
  },
  '/macro': {
    title: 'Macro Dashboard',
    description: "Track the broader economic environment that affects all investments. Monitor Fed policy, economic indicators, and market sentiment in one place.",
    tips: [
      'The yield curve indicator is an important recession signal.',
      'VIX above 25 typically indicates elevated market fear.',
      'Track Fed Funds rate to understand monetary policy direction.',
    ],
    whatsNew: [
      'New: Macro narrative explaining current conditions',
    ],
  },
  '/earnings': {
    title: 'Earnings Hub',
    description: "Track upcoming and recent earnings reports. See analyst estimates, historical beats/misses, and the expected market impact of each report.",
    tips: [
      'Earnings surprise history shows how stocks typically react.',
      'Filter by your watchlist to focus on stocks you own.',
      'Toggle between story cards and table view for different perspectives.',
    ],
    whatsNew: [
      'New: Earnings story cards with narrative context',
    ],
  },
};

// Inline tooltip for technical terms
interface TermTooltipProps {
  term: string;
  definition: string;
  children: React.ReactNode;
}

export function TermTooltip({ term, definition, children }: TermTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span
      className="term-tooltip-wrapper"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <HelpCircle size={12} className="term-tooltip-icon" />
      {isVisible && (
        <span className="term-tooltip-popup">
          <strong>{term}</strong>
          <span>{definition}</span>
        </span>
      )}
    </span>
  );
}

// Common financial term definitions
export const TERM_DEFINITIONS: Record<string, string> = {
  'Cipher Score': 'A proprietary 0-100 rating combining momentum, fundamentals, sentiment, insider activity, and causal event signals.',
  'VIX': 'The CBOE Volatility Index, measuring expected market volatility over the next 30 days. Higher values indicate more fear.',
  'P/E Ratio': 'Price-to-Earnings ratio. Stock price divided by earnings per share. Lower may indicate value, higher may indicate growth expectations.',
  'RSI': 'Relative Strength Index. A momentum indicator ranging 0-100. Above 70 is overbought, below 30 is oversold.',
  'MACD': 'Moving Average Convergence Divergence. A trend-following momentum indicator showing relationship between two moving averages.',
  'Yield Curve': 'The difference between long-term and short-term Treasury yields. Inversion (negative) historically precedes recessions.',
  'Alpha': 'Return above the benchmark. Positive alpha means outperformance.',
  'Sharpe Ratio': 'Risk-adjusted return. Higher is better. Above 1.0 is good, above 2.0 is excellent.',
  'Max Drawdown': 'The largest peak-to-trough decline. Lower is better for managing risk.',
  'Market Cap': 'Total market value of a company. Calculated as share price times shares outstanding.',
  'EPS': 'Earnings Per Share. Net income divided by shares outstanding.',
  'Revenue Growth': 'Year-over-year increase in total sales. Higher growth often commands premium valuations.',
};

// Main contextual help button and panel
export function ContextualHelpButton() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Get help content for current page
  const getPageHelp = (): PageHelp => {
    // Check for exact match first
    if (PAGE_HELP[location.pathname]) {
      return PAGE_HELP[location.pathname];
    }

    // Check for stock detail page
    if (location.pathname.startsWith('/stock/')) {
      return {
        title: 'Stock Analysis',
        description: "Comprehensive analysis of an individual stock. View fundamental data, technical indicators, news sentiment, insider activity, and our proprietary Cipher Score breakdown.",
        tips: [
          'The Story tab provides a narrative overview of the stock.',
          'Cipher Score breakdown shows which factors are strongest.',
          'News impact analysis shows how headlines affected the price.',
        ],
        whatsNew: [
          'New: Story tab with narrative-driven analysis',
        ],
      };
    }

    // Default help
    return {
      title: 'Cipher Intelligence',
      description: "Cipher provides AI-powered stock market intelligence. Use the navigation to explore different features.",
      tips: [
        'Use Command+K (or Ctrl+K) to quickly search for any stock.',
        'Your personalized experience improves as you use the app.',
        'Set up alerts to stay informed without constantly checking.',
      ],
    };
  };

  const help = getPageHelp();

  return (
    <>
      <button
        className="contextual-help-btn"
        onClick={() => setIsOpen(true)}
        title="Help"
      >
        <HelpCircle size={18} />
      </button>

      {isOpen && (
        <div className="help-panel-overlay" onClick={() => setIsOpen(false)}>
          <div className="help-panel" onClick={e => e.stopPropagation()}>
            <button className="help-panel-close" onClick={() => setIsOpen(false)} aria-label="Close help">
              <X size={18} />
            </button>

            <div className="help-panel-header">
              <Info size={20} />
              <h3>{help.title}</h3>
            </div>

            <p className="help-description">{help.description}</p>

            <div className="help-section">
              <div className="help-section-header">
                <Lightbulb size={16} />
                <span>Tips</span>
              </div>
              <ul className="help-tips">
                {help.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>

            {help.whatsNew && help.whatsNew.length > 0 && (
              <div className="help-section whats-new">
                <div className="help-section-header">
                  <Sparkles size={16} />
                  <span>What's New</span>
                </div>
                <ul className="help-tips">
                  {help.whatsNew.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="help-footer">
              <a href="#" className="help-link">
                <BookOpen size={14} />
                Full Documentation
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ContextualHelpButton;
